import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { ChevronRight, CircleCheckBig, Mountain, ScanLine } from 'lucide-react';
import {
  SHEET_HEIGHT_MM,
  SHEET_WIDTH_MM,
  posterLayout,
  type PosterLayout,
  type QrPosterFormat,
} from './page-format';
import { QR_POSTER_THEMES, type QrPosterTheme, type QrPosterThemeId } from './poster-theme';

/**
 * The printable poster a gym, crag or competition hangs on its wall: who the
 * place is, the code that opens its page, and the three steps that follow.
 *
 * Every rule here is an inline style, and deliberately so — the sheet is
 * serialised into a bare iframe to be printed (see `printNode`), which carries
 * the DOM but none of the app's stylesheets. Anything expressed as a class
 * would arrive on paper unstyled.
 *
 * There are two compositions, and which one is used follows from the size.
 * A4 has room to explain itself, so it carries the copy. A5 and A6 are cut two
 * and four to a sheet and read in the hand: shrinking the A4 down would take
 * its 8pt body text to around 5pt, so they get their own layout instead —
 * fewer words, far more air, and type sized for what it becomes once reduced.
 */

const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif";

/** Sits in the middle of the code, which level H redundancy can spare. */
const DIVEPEAKS_MARK = '/icons/icon-192x192.svg';

/**
 * The light margin a scanner wants around the modules. It is why the code sits
 * on a card of its own: on the dark theme the sheet would otherwise run right
 * up to them, and a code with no quiet zone is a code that does not read.
 */
const QUIET_ZONE_MM = 4;

interface LocationQrPosterProps {
  /** The gym, crag or competition the code opens. */
  name: string;
  /** The line under the name — where the place is, or when the event runs. */
  subtitle?: string | null;
  logoUrl?: string | null;
  /** The absolute URL encoded in the code. */
  url: string;
  /** The same URL as it reads on paper: no scheme, no query, no trailing slash. */
  displayUrl: string;
  /**
   * Which page of the place the code opens, when it is not the place's own —
   * a wall, the boulders, the routes. Printed as a pill under the name, which
   * is what tells a poster hung beside a board which board it belongs to.
   */
  targetLabel?: string | null;
  format: QrPosterFormat;
  theme: QrPosterThemeId;
}

type SheetProps = Omit<LocationQrPosterProps, 'format' | 'theme'> & { theme: QrPosterTheme };

/**
 * A long name has to shrink or it wraps into whatever is under it. The
 * thresholds are where a name stops fitting the sheet's text width.
 */
function nameFontSize(name: string, base: number): string {
  if (name.length > 30) return `${(base * 0.66).toFixed(1)}pt`;
  if (name.length > 22) return `${(base * 0.81).toFixed(1)}pt`;
  return `${base}pt`;
}

/** The DivePeaks mark, which is a fixed green SVG the mono theme has to mute. */
function Mark({ theme, size }: { theme: QrPosterTheme; size: string }) {
  return (
    <img
      src={DIVEPEAKS_MARK}
      alt=""
      style={{ display: 'block', width: size, height: size, filter: theme.markFilter }}
    />
  );
}

/** The place's logo, or its initial when it has none. */
function LocationMark({
  name,
  logoUrl,
  theme,
  size,
}: {
  name: string;
  logoUrl?: string | null;
  theme: QrPosterTheme;
  size: string;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          background: theme.qrCard,
          border: `0.5mm solid ${theme.qrCard}`,
          boxShadow: `0 0 0 0.35mm ${theme.line}`,
        }}
      />
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        background: theme.qrCard,
        border: `0.5mm solid ${theme.accent}`,
        color: theme.accent,
        fontSize: '20pt',
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/** The pill naming the wall, or the list, that the code opens. */
function TargetPill({ label, theme, fontSize }: { label: string; theme: QrPosterTheme; fontSize: string }) {
  return (
    <span
      style={{
        padding: '1mm 3mm',
        borderRadius: '4mm',
        border: `0.35mm solid ${theme.accent}`,
        color: theme.accent,
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        lineHeight: 1.2,
      }}
    >
      {label}
    </span>
  );
}

/**
 * The code on its own light card. `sizeMm` is the card; the modules get what
 * is left of it once the quiet zone is taken out.
 */
function QrCard({
  url,
  theme,
  sizeMm,
  markSizeMm,
}: {
  url: string;
  theme: QrPosterTheme;
  sizeMm: number;
  markSizeMm: number;
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: `${sizeMm}mm`,
        height: `${sizeMm}mm`,
        padding: `${QUIET_ZONE_MM}mm`,
        background: theme.qrCard,
        borderRadius: '2mm',
      }}
    >
      {/*
        Level H tolerates roughly a third of the code being covered, which is
        what lets the mark sit in the middle of it.
      */}
      <QRCode
        value={url}
        size={512}
        level="H"
        bgColor={theme.qrCard}
        fgColor={theme.qrFg}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          padding: '1mm',
          borderRadius: '1.5mm',
          background: theme.qrCard,
        }}
      >
        <Mark theme={theme} size={`${markSizeMm}mm`} />
      </span>
    </div>
  );
}

/** The step number, drawn as a ring so it survives a printer with no colour. */
function StepBadge({ step, theme }: { step: number; theme: QrPosterTheme }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '7mm',
        height: '7mm',
        flexShrink: 0,
        borderRadius: '50%',
        border: `0.5mm solid ${theme.accent}`,
        color: theme.accent,
        fontSize: '10pt',
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  );
}

function stepTitleStyle(theme: QrPosterTheme): CSSProperties {
  return {
    margin: 0,
    fontSize: '11pt',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: 1.1,
    color: theme.ink,
  };
}

function stepBodyStyle(theme: QrPosterTheme): CSSProperties {
  return { margin: 0, fontSize: '8pt', lineHeight: 1.45, color: theme.body };
}

/** One of the two cards under the code on the full sheet. */
function SecondaryStep({
  step,
  icon,
  title,
  body,
  theme,
}: {
  step: number;
  icon: ReactNode;
  title: string;
  body: string;
  theme: QrPosterTheme;
}) {
  return (
    <div
      style={{
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '2.5mm',
        padding: '4mm',
        border: `0.35mm solid ${theme.line}`,
        borderRadius: '3mm',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <StepBadge step={step} theme={theme} />
        {icon}
      </div>
      <h3 style={stepTitleStyle(theme)}>{title}</h3>
      <p style={stepBodyStyle(theme)}>{body}</p>
    </div>
  );
}

/** The brand rule across the top, the one full-bleed element of a sheet. */
function BrandRule({ theme }: { theme: QrPosterTheme }) {
  return (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3.5mm', background: theme.accent }}
    />
  );
}

function sheetStyle(theme: QrPosterTheme): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.sheet,
    color: theme.ink,
    overflow: 'hidden',
  };
}

/** The A4 sheet: room enough to say what each step actually involves. */
function FullSheet({ name, subtitle, logoUrl, url, displayUrl, targetLabel, theme }: SheetProps) {
  const { t } = useTranslation();

  return (
    <div style={sheetStyle(theme)}>
      <BrandRule theme={theme} />

      {/* Whose wall this is hanging on — a block of its own, so the name reads
          as the poster's owner rather than as its first line. */}
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '9mm 10mm 6mm',
          background: theme.headerBg,
          borderBottom: `0.35mm solid ${theme.line}`,
        }}
      >
        <LocationMark name={name} logoUrl={logoUrl} theme={theme} size="20mm" />
        <h1
          style={{
            margin: '2.5mm 0 0',
            fontSize: nameFontSize(name, 21),
            fontWeight: 800,
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
            color: theme.ink,
          }}
        >
          {name}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: '1.5mm 0 0',
              fontSize: '8pt',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: theme.muted,
            }}
          >
            {subtitle}
          </p>
        )}
        {targetLabel && (
          <span style={{ marginTop: '2.5mm' }}>
            <TargetPill label={targetLabel} theme={theme} fontSize="8pt" />
          </span>
        )}
      </header>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '5mm 10mm 8mm' }}>
        {/* What the poster is actually asking of whoever reads it. */}
        <div style={{ textAlign: 'center' }}>
          <h2
            style={{
              margin: 0,
              fontSize: '16pt',
              fontWeight: 800,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              lineHeight: 1.15,
              color: theme.ink,
            }}
          >
            {t('qrPoster.poster.headline')}
          </h2>
          <div
            aria-hidden="true"
            style={{
              width: '14mm',
              height: '1mm',
              margin: '2.5mm auto 0',
              background: theme.accent,
              borderRadius: '1mm',
            }}
          />
          <p style={{ margin: '2.5mm auto 0', maxWidth: '108mm', fontSize: '8.5pt', lineHeight: 1.45, color: theme.body }}>
            {t('qrPoster.poster.intro')}
          </p>
        </div>

        {/* Step one is the code itself, so it gets the width. */}
        <section
          style={{
            marginTop: '4mm',
            display: 'flex',
            alignItems: 'center',
            gap: '5mm',
            padding: '5mm',
            border: `0.35mm solid ${theme.line}`,
            borderRadius: '3mm',
          }}
        >
          <div style={{ flexShrink: 0, width: '42mm' }}>
            <QrCard url={url} theme={theme} sizeMm={42} markSizeMm={7} />
            <p
              style={{
                margin: '2mm 0 0',
                fontSize: '6.5pt',
                lineHeight: 1.3,
                textAlign: 'center',
                color: theme.muted,
                overflowWrap: 'anywhere',
              }}
            >
              {displayUrl}
            </p>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5mm' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '3mm' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
                <StepBadge step={1} theme={theme} />
                <h3 style={stepTitleStyle(theme)}>{t('qrPoster.poster.step1Title')}</h3>
              </div>
              <ScanLine size={34} color={theme.accent} strokeWidth={1.5} />
            </div>
            <p style={stepBodyStyle(theme)}>{t('qrPoster.poster.step1Body')}</p>
          </div>
        </section>

        <div style={{ marginTop: '3.5mm', display: 'flex', gap: '4mm' }}>
          <SecondaryStep
            step={2}
            icon={<Mountain size={34} color={theme.accent} strokeWidth={1.5} />}
            title={t('qrPoster.poster.step2Title')}
            body={t('qrPoster.poster.step2Body')}
            theme={theme}
          />
          <SecondaryStep
            step={3}
            icon={<CircleCheckBig size={34} color={theme.accent} strokeWidth={1.5} />}
            title={t('qrPoster.poster.step3Title')}
            body={t('qrPoster.poster.step3Body')}
            theme={theme}
          />
        </div>

        {/* `auto` eats whatever vertical slack the sheet has left. */}
        <footer
          style={{
            marginTop: 'auto',
            paddingTop: '5mm',
            borderTop: `0.35mm solid ${theme.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '4mm',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '2.5mm' }}>
            <Mark theme={theme} size="7mm" />
            <div>
              <p style={{ margin: 0, fontSize: '10pt', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.1, color: theme.ink }}>
                DivePeaks
              </p>
              <p style={{ margin: '0.5mm 0 0', fontSize: '7pt', color: theme.muted, lineHeight: 1.1 }}>
                app.divepeaks.com
              </p>
            </div>
          </div>
          <p style={{ margin: 0, maxWidth: '62mm', fontSize: '7.5pt', lineHeight: 1.35, textAlign: 'right', color: theme.muted }}>
            {t('qrPoster.poster.footerTagline')}
          </p>
        </footer>
      </div>
    </div>
  );
}

/** One of the three words on the compact sheet: an icon in a ring, then the step. */
function CompactStep({ icon, label, theme }: { icon: ReactNode; label: string; theme: QrPosterTheme }) {
  return (
    <div
      style={{
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3mm',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '15mm',
          height: '15mm',
          borderRadius: '50%',
          border: `0.6mm solid ${theme.accent}`,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          fontSize: '12pt',
          fontWeight: 800,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          lineHeight: 1.1,
          color: theme.ink,
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * The A5 and A6 sheet: the code, whose it is, and three words. Every size on
 * it is set for what it becomes once cut four to a page, which is why the
 * explaining is left to the A4.
 */
function CompactSheet({ name, subtitle, logoUrl, url, displayUrl, targetLabel, theme }: SheetProps) {
  const { t } = useTranslation();
  const chevron = (
    <ChevronRight size={20} color={theme.muted} strokeWidth={2} style={{ marginTop: '5mm', flexShrink: 0 }} />
  );

  return (
    <div style={sheetStyle(theme)}>
      <BrandRule theme={theme} />

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '10mm 9mm 8mm',
        }}
      >
        <LocationMark name={name} logoUrl={logoUrl} theme={theme} size="22mm" />
        <h1
          style={{
            margin: '3.5mm 0 0',
            fontSize: nameFontSize(name, 24),
            fontWeight: 800,
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
            color: theme.ink,
          }}
        >
          {name}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: '2mm 0 0',
              fontSize: '9.5pt',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: theme.muted,
            }}
          >
            {subtitle}
          </p>
        )}
        {targetLabel && (
          <span style={{ marginTop: '3mm' }}>
            <TargetPill label={targetLabel} theme={theme} fontSize="9.5pt" />
          </span>
        )}

        <h2
          style={{
            margin: '5mm 0 0',
            fontSize: '15pt',
            fontWeight: 800,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            lineHeight: 1.15,
            color: theme.ink,
          }}
        >
          {t('qrPoster.poster.headline')}
        </h2>
        <div
          aria-hidden="true"
          style={{ width: '16mm', height: '1.2mm', margin: '3mm 0 0', background: theme.accent, borderRadius: '1mm' }}
        />

        <div style={{ marginTop: '5mm' }}>
          <QrCard url={url} theme={theme} sizeMm={66} markSizeMm={11} />
        </div>
        <p
          style={{
            margin: '3mm 0 0',
            fontSize: '8.5pt',
            lineHeight: 1.3,
            color: theme.muted,
            overflowWrap: 'anywhere',
          }}
        >
          {displayUrl}
        </p>

        {/* Three words and nothing else: at this size a sentence would be a
            smudge, and the code is what the reader is here for anyway. */}
        <div style={{ marginTop: '6mm', display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <CompactStep
            icon={<ScanLine size={30} color={theme.accent} strokeWidth={1.6} />}
            label={t('qrPoster.poster.step1Title')}
            theme={theme}
          />
          {chevron}
          <CompactStep
            icon={<Mountain size={30} color={theme.accent} strokeWidth={1.6} />}
            label={t('qrPoster.poster.step2Title')}
            theme={theme}
          />
          {chevron}
          <CompactStep
            icon={<CircleCheckBig size={30} color={theme.accent} strokeWidth={1.6} />}
            label={t('qrPoster.poster.step3Title')}
            theme={theme}
          />
        </div>

        <footer
          style={{
            marginTop: 'auto',
            paddingTop: '6mm',
            width: '100%',
            borderTop: `0.35mm solid ${theme.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2.5mm',
          }}
        >
          <Mark theme={theme} size="8mm" />
          <span style={{ fontSize: '12pt', fontWeight: 800, letterSpacing: '-0.01em', color: theme.ink }}>
            DivePeaks
          </span>
        </footer>
      </div>
    </div>
  );
}

/**
 * The dashed rectangle each copy is cut out along. One per copy rather than a
 * single line shared down the middle: the gutter between them is what gives
 * the scissors room, so both neighbours keep an edge of their own.
 */
function CutMarks({ layout, theme }: { layout: PosterLayout; theme: QrPosterTheme }) {
  return (
    <div aria-hidden="true" data-testid="qr-poster-cut-marks" style={{ position: 'absolute', inset: 0 }}>
      {layout.copies.map((copy, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `${copy.leftMm}mm`,
            top: `${copy.topMm}mm`,
            width: `${layout.sheetWidthMm}mm`,
            height: `${layout.sheetHeightMm}mm`,
            border: `0.3mm dashed ${theme.cut}`,
          }}
        />
      ))}
    </div>
  );
}

const LocationQrPoster = forwardRef<HTMLDivElement, LocationQrPosterProps>(
  function LocationQrPoster({ format, theme: themeId, ...sheet }, ref) {
    const layout = posterLayout(format);
    const theme = QR_POSTER_THEMES[themeId];
    const Sheet = format === 'a4' ? FullSheet : CompactSheet;

    return (
      <div
        ref={ref}
        data-testid="qr-poster-page"
        style={{
          position: 'relative',
          width: `${layout.spec.pageWidthMm}mm`,
          height: `${layout.spec.pageHeightMm}mm`,
          overflow: 'hidden',
          // The paper, not the poster: the margin and the gutters between the
          // copies are what is left of the sheet once they are cut out.
          background: '#ffffff',
          fontFamily: FONT_STACK,
        }}
      >
        {layout.copies.map((copy, index) => (
          <div
            key={index}
            data-testid="qr-poster-copy"
            style={{
              position: 'absolute',
              left: `${copy.leftMm}mm`,
              top: `${copy.topMm}mm`,
              width: `${layout.sheetWidthMm}mm`,
              height: `${layout.sheetHeightMm}mm`,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: `${SHEET_WIDTH_MM}mm`,
                height: `${SHEET_HEIGHT_MM}mm`,
                transform: `scale(${layout.scale})`,
                transformOrigin: 'top left',
              }}
            >
              <Sheet {...sheet} theme={theme} />
            </div>
          </div>
        ))}

        <CutMarks layout={layout} theme={theme} />
      </div>
    );
  }
);

export default LocationQrPoster;
